import React, { useState, useRef, useEffect } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  basicInfoQuestions,
  additionalAllowancesWorksheetQuestion,
  step3Question,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function IllinoisForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  const basicInfoForm = useRef();
  const basicInfoInitQ = getInitialQuestions(basicInfoQuestions);
  const [basicInfo, setBasicInfo] = useState(basicInfoInitQ);
  const [numDependents, setNumDependents] = useState(data.num_dependents || '');

  const [basicAllowancesClaimed, setBasicAllowancesClaimed] = useState(
    data.basic_allowances_claimed || 0,
  );

  const [basicAllowancesError, setBasicAllowancesError] = useState(false);
  const [basicAllowancesValueError, setBasicAllowancesValueError] = useState(false);

  const [notDependent, setNotDependent] = useState(
    data.cannot_be_claimed_as_dependent || false,
  );

  const [spouseDependent, setSpouseDependent] = useState(
    data.claim_spouse_as_dependent || false,
  );

  const additionalAllowancesForm = useRef();
  const additionalAllowancesInitQ = getInitialQuestions(
    additionalAllowancesWorksheetQuestion,
  );
  const [additionalAllowances, setAdditionalAllowances] = useState(
    additionalAllowancesInitQ,
  );

  const [additionalAllowancesClaimed, setAdditionalAllowancesClaimed] = useState(data.additional_allowances_claimed || 0);

  const [additionalAllowancesError, setAdditionalAllowancesError] = useState(false);
  const [additionalAllowancesValueError, setAdditionalAllowancesValueError] = useState(false);

  const [selfAge65, setSelfAge65] = useState(data.self_age_65 || false);
  const [selfBlind, setSelfBlind] = useState(data.self_blind || false);
  const [spouseAge65, setSpouseAge65] = useState(data.spouse_age_65 || false);
  const [spouseBlind, setSpouseBlind] = useState(data.spouse_blind || false);

  const step3Form = useRef();
  const step3InitQ = getInitialQuestions(step3Question);
  const [step3Q, setStep3Q] = useState(step3InitQ);

  const [exemptFromTaxWithholding, setExemptFromTaxWithholding] = useState(
    data.exempt_from_tax_withholding || false,
  );

  const basicAllowancesEntitled = (() => {
    let numBoxesChecked = 0;
    if (notDependent) numBoxesChecked += 1;
    if (spouseDependent) numBoxesChecked += 1;

    const line3 = numBoxesChecked + (parseInt(numDependents, 10) || 0);
    return line3;
  })();

  const additionalAllowancesEntitled = (() => {
    let numBoxesChecked = 0;
    if (selfAge65) numBoxesChecked += 1;
    if (selfBlind) numBoxesChecked += 1;
    if (spouseAge65) numBoxesChecked += 1;
    if (spouseBlind) numBoxesChecked += 1;

    const line7 = Math.round(
      parseInt(
        additionalAllowances[0].answer.answer.line_4_with_deductions || 0,
      ) / 1000,
    );
    const line8 = numBoxesChecked + line7;
    return line8;
  })();

  const renderBasicInfoForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfo.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfo(questions);
        }}
        ref={basicInfoForm}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I am exempt from federal and Illinois Income Tax withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={exemptFromTaxWithholding}
                checked={exemptFromTaxWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setExemptFromTaxWithholding(!exemptFromTaxWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!exemptFromTaxWithholding}
                checked={!exemptFromTaxWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setExemptFromTaxWithholding(!exemptFromTaxWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </Flex>
  );

  const renderBasicAllowancesForm = (isReviewStep) => (
    <Flex column>
      <h3 style={{ marginBottom: 20 }}>Basic personal allowances</h3>
      <Flex column>
        <S.QuestionLabel mb="7px">
          Check all that apply:
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="checkbox"
                value={notDependent}
                checked={notDependent}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setNotDependent(!notDependent);
                }}
              />
              <Box ml={2} />
              No one else can claim me as a dependent.
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="checkbox"
                value={spouseDependent}
                checked={spouseDependent}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setSpouseDependent(!spouseDependent);
                }}
              />
              <Box ml={2} />
              I can claim my spouse as a dependent
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
      <S.QuestionLabel mb="7px">
        Enter the number of dependents (other than you or your spouse) you will
        claim on your tax return (Step 1.2 of worksheet)
      </S.QuestionLabel>
      <S.QuestionWrapper>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={numDependents}
            disabled={isReviewStep}
            onChange={(e) => {
              e.target.value === ''
                ? setNumDependents(e.target.value)
                : setNumDependents(parseInt(e.target.value));
            }}
          />
        </Flex>
      </S.QuestionWrapper>
      <S.QuestionLabel mb="7px">
        This is the total number of basic personal allowances to which you are
        entitled. You are not required to claim these allowances. (Step 1.3 of
        worksheet)
      </S.QuestionLabel>
      <S.QuestionWrapper>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={basicAllowancesEntitled}
            disabled
          />
        </Flex>
      </S.QuestionWrapper>
      <S.QuestionLabel mb="7px">
        Enter the total number of basic personal allowances you choose to claim
        (Step 1.4 of worksheet) (Line 1 of Form IL-W-4)
      </S.QuestionLabel>
      <S.QuestionWrapper>
        <Flex>
          <S.Input
            type="text"
            value={basicAllowancesClaimed}
            onChange={(e) => {
              e.target.value === ''
                ? setBasicAllowancesClaimed(e.target.value)
                : setBasicAllowancesClaimed(parseInt(e.target.value));
            }}
            disabled={isReviewStep}
            style={{
              borderColor:
                basicAllowancesError || basicAllowancesValueError ? 'red' : '',
            }}
          />
        </Flex>
      </S.QuestionWrapper>
      {basicAllowancesError && (
        <Flex justifyContent="flex-start">
          <Text color="red">
            You may not claim more allowances than the amount of basic personal
            allowances to which you are entitled
          </Text>
        </Flex>
      )}
      {basicAllowancesValueError && (
        <Flex justifyContent="flex-start">
          <Text color="red">Please enter a valid response</Text>
        </Flex>
      )}
    </Flex>
  );

  const renderAdditionalAllowancesForm = (isReviewStep) => (
    <Flex column>
      <h3 style={{ marginBlock: 20 }}>Additional allowances</h3>
      <Flex column>
        <S.QuestionLabel mb="7px">Check all that apply:</S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="checkbox"
                value={selfAge65}
                checked={selfAge65}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setSelfAge65(!selfAge65);
                }}
              />
              <Box ml={2} />
              I am 65 or older.
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="checkbox"
                value={selfBlind}
                checked={selfBlind}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setSelfBlind(!selfBlind);
                }}
              />
              <Box ml={2} />
              I am legally blind.
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="checkbox"
                value={spouseAge65}
                checked={spouseAge65}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setSpouseAge65(!spouseAge65);
                }}
              />
              <Box ml={2} />
              My spouse is 65 or older.
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="checkbox"
                value={spouseBlind}
                checked={spouseBlind}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setSpouseBlind(!spouseBlind);
                }}
              />
              <Box ml={2} />
              My spouse is legally blind.
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
      <FormConsumer
        questions={additionalAllowances}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = additionalAllowances.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAdditionalAllowances(questions);
        }}
        ref={additionalAllowancesForm}
      />
      <S.QuestionLabel mb="7px">
        This is the total number of additional allowances to which you are
        entitled. You are not required to claim these allowances. (Step 2.8 of
        worksheet)
      </S.QuestionLabel>
      <S.QuestionWrapper>
        <Flex mb="10px">
          <S.Input type="text" value={additionalAllowancesEntitled} disabled />
        </Flex>
      </S.QuestionWrapper>
      <S.QuestionLabel mb="7px">
        Enter the total number of additional allowances you elect to claim (Step
        2.9 of worksheet) (Line 2 of Form IL-W-4)
      </S.QuestionLabel>
      <S.QuestionWrapper>
        <Flex>
          <S.Input
            type="text"
            value={additionalAllowancesClaimed}
            onChange={(e) => {
              e.target.value === ''
                ? setAdditionalAllowancesClaimed(e.target.value)
                : setAdditionalAllowancesClaimed(parseInt(e.target.value));
            }}
            disabled={isReviewStep}
            style={{
              borderColor:
                additionalAllowancesError || additionalAllowancesValueError
                  ? 'red'
                  : '',
            }}
          />
        </Flex>
      </S.QuestionWrapper>
      {additionalAllowancesError && (
        <Flex justifyContent="flex-start">
          <Text color="red">
            You may not claim more allowances than the amount of additional
            allowances to which you are entitled
          </Text>
        </Flex>
      )}
      {additionalAllowancesValueError && (
        <Flex justifyContent="flex-start">
          <Text color="red">Please enter a valid response</Text>
        </Flex>
      )}
    </Flex>
  );

  const renderAllowancesQuestions = (isReviewStep) => (
    <Flex column>
      {renderBasicAllowancesForm(isReviewStep)}
      {renderAdditionalAllowancesForm(isReviewStep)}

      <h3 style={{ marginBlock: 20 }}>Additional withholding amount</h3>
      <FormConsumer
        questions={step3Q}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = step3Q.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStep3Q(questions);
        }}
        ref={step3Form}
      />
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {!exemptFromTaxWithholding && renderAllowancesQuestions()}
        </Flex>
        {viewResultAsManager
          && companyStaff.pii_permission === 'full_access' && (
            <Flex column mt={10} mb={10}>
              <Flex alignItems="center">
                <Text mr={16}>Download signed documents</Text>
                <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                  <img src="/j/images/onboarding/download.svg" />
                </a>
              </Flex>
            </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (basicInfoForm && basicInfoForm.current) {
              basicInfoForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (additionalAllowancesForm && additionalAllowancesForm.current) {
              additionalAllowancesForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (step3Form && step3Form.current) {
              step3Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (!exemptFromTaxWithholding) {
              if (!/^\d*$/.test(basicAllowancesClaimed)) {
                err = true;
                setBasicAllowancesValueError(true);
              } else {
                setBasicAllowancesValueError(false);
              }
              if (basicAllowancesClaimed > basicAllowancesEntitled) {
                err = true;
                setBasicAllowancesError(true);
              } else {
                setBasicAllowancesError(false);
              }
              if (!/^\d*$/.test(additionalAllowancesClaimed)) {
                err = true;
                setAdditionalAllowancesValueError(true);
              } else {
                setAdditionalAllowancesValueError(false);
              }
              if (additionalAllowancesClaimed > additionalAllowancesEntitled) {
                err = true;
                setAdditionalAllowancesError(true);
              } else {
                setAdditionalAllowancesError(false);
              }
            }
            if (err) {
            } else {
              // No error, proceed to save
              const basicInfoData = basicInfo.reduce(
                (acc, question) => ({ ...acc, ...question.answer.answer }),
                {},
              );
              const exemptionStatusData = {
                exempt_from_tax_withholding: exemptFromTaxWithholding,
              };

              let numDependentsTemp = '';
              let additionalAllowancesData;

              let basicAllowancesClaimedTemp = 0;
              let basicAllowancesEntitledTemp = 0;
              let additionalAllowancesClaimedTemp = 0;
              let additionalAllowancesEntitledTemp = 0;

              let notDependentTemp = false;
              let spouseDependentTemp = false;

              let selfAge65Temp = false;
              let selfBlindTemp = false;
              let spouseAge65Temp = false;
              let spouseBlindTemp = false;

              let step3Data;

              if (exemptFromTaxWithholding) {
                additionalAllowancesData = resetInitialQuestions(
                  additionalAllowancesWorksheetQuestion,
                ).reduce(
                  (acc, question) => ({ ...acc, ...question.answer.answer }),
                  {},
                );
                step3Data = resetInitialQuestions(step3Question).reduce(
                  (acc, question) => ({ ...acc, ...question.answer.answer }),
                  {},
                );
              } else {
                numDependentsTemp = numDependents;

                additionalAllowancesData = additionalAllowances.reduce(
                  (acc, question) => ({ ...acc, ...question.answer.answer }),
                  {},
                );

                notDependentTemp = notDependent;
                spouseDependentTemp = spouseDependent;

                selfAge65Temp = selfAge65;
                selfBlindTemp = selfBlind;
                spouseAge65Temp = spouseAge65;
                spouseBlindTemp = spouseBlind;

                step3Data = step3Q.reduce(
                  (acc, question) => ({ ...acc, ...question.answer.answer }),
                  {},
                );

                basicAllowancesClaimedTemp = basicAllowancesClaimed;
                basicAllowancesEntitledTemp = basicAllowancesEntitled;
                additionalAllowancesClaimedTemp = additionalAllowancesClaimed;
                additionalAllowancesEntitledTemp = additionalAllowancesEntitled;
              }

              const basicMultipleSelectData = {
                cannot_be_claimed_as_dependent: notDependentTemp,
                claim_spouse_as_dependent: spouseDependentTemp,
              };

              const ageBlindnessData = {
                self_age_65: selfAge65Temp,
                self_blind: selfBlindTemp,
                spouse_age_65: spouseAge65Temp,
                spouse_blind: spouseBlindTemp,
              };

              const allowanceValuesData = {
                num_dependents: numDependentsTemp,
                basic_allowances_claimed: basicAllowancesClaimedTemp,
                basic_allowances_entitled: basicAllowancesEntitledTemp,
                additional_allowances_claimed: additionalAllowancesClaimedTemp,
                additional_allowances_entitled:
                  additionalAllowancesEntitledTemp,
              };
              const allExemptionData = {
                ...exemptionStatusData,
              };
              const allAllowancesData = {
                ...allowanceValuesData,
                ...additionalAllowancesData,
                ...basicMultipleSelectData,
                ...ageBlindnessData,
              };
              const data = {
                ...basicInfoData,
                ...allExemptionData,
                ...allAllowancesData,
                ...step3Data,
              };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step2);
                scrollTop();
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {!exemptFromTaxWithholding && renderAllowancesQuestions(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default IllinoisForm;
